import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_URL } from 'app/helpers/globals';
import { BehaviorSubject, Observable } from 'rxjs';
import { share, map, tap, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

export type NameFormatResponse = {
  msg: string;
  firstnameFirst?: boolean;
};

export type SaveNameFormatResponse = {
  msg: string;
};

@Injectable({
  providedIn: 'root'
})
export class NameFormatService {

  private firstnameFirstSubject = new BehaviorSubject<boolean>(true);
  public firstnameFirst$ = this.firstnameFirstSubject.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService
  ) {
    // Subscribe to company changes and update name format settings
    this.authService.currentCompanyId.pipe(
      switchMap(companyId => {
        if (companyId && companyId !== 0) {
          return this.getNameFormatSettings(companyId);
        }
        return new Observable<NameFormatResponse>(observer => {
          observer.next({ msg: 'ok', firstnameFirst: true });
          observer.complete();
        });
      })
    ).subscribe(response => {
      if (response.firstnameFirst !== undefined) {
        this.firstnameFirstSubject.next(response.firstnameFirst);
      }
    });
  }

  saveNameFormatSettings(clientId: number, firstnameFirst: boolean, forAll: boolean = false): Observable<SaveNameFormatResponse> {
    const url = `${API_URL}company/saveNameFormatSettings?forAll=${forAll}&firstnameFirst=${firstnameFirst}&clientId=${clientId}`;
    return this.http.post<SaveNameFormatResponse>(url, {}, { withCredentials: true })
      .pipe(
        share(),
        tap(() => {
          // Update local state after successful save
          this.firstnameFirstSubject.next(firstnameFirst);
        }),
        map((response) => response)
      );
  }

  getNameFormatSettings(clientId: number): Observable<NameFormatResponse> {
    const url = `${API_URL}company/getNameFormatSettings?clientId=${clientId}`;
    return this.http.get<NameFormatResponse>(url, { withCredentials: true })
      .pipe(
        share(),
        tap((response) => {
          if (response.firstnameFirst !== undefined) {
            this.firstnameFirstSubject.next(response.firstnameFirst);
          }
        }),
        map((response) => response)
      );
  }

  getCurrentFirstnameFirst(): boolean {
    return this.firstnameFirstSubject.value;
  }

  formatName(firstname: string, lastname: string): string {
    if (!firstname && !lastname) {
      return '';
    }
    
    if (!firstname) {
      return lastname || '';
    }
    
    if (!lastname) {
      return firstname || '';
    }

    const firstnameFirst = this.getCurrentFirstnameFirst();
    
    if (firstnameFirst) {
      return `${firstname} ${lastname}`.trim();
    } else {
      return `${lastname} ${firstname}`.trim();
    }
  }
}
